<template>
  <section class="h-full px-4 gap-6 overflow-hidden grid grid-rows-[auto_auto_1fr] my-4">
    <h2 class="text-lg font-medium text-gray-800">{{ $t('pages.app-preferences') }}</h2>

    <div v-if="isModifiedPreferences" class="flex">
      <BaseButton outline @click="onDiscardClick" size="sm" class="m-2 capitalize">
        <slot>{{ $t('messages.dismissChanges') }} </slot>
      </BaseButton>
      <BaseButton @click="onSaveAll" size="sm" class="m-2 capitalize">
        <slot>{{ $t('common.actions.save') }} </slot>
      </BaseButton>
    </div>

    <div class="overflow-hidden">
      <BaseTable
        v-if="modifiedAppPreferences"
        :headers="tableHeaders"
        :list="modifiedAppPreferences"
        :is-add-new-hidden="true"
        table-id="appPreferences"
        :group-by-headers="[
          {
            value: 'mwParamGroup.name',
            text: 'Group',
            type: 'text',
            active: true,
          },
        ]"
        clickable
        @table-item-clicked="onItemClick"
        @refresh-data="loadData"
        @apply-filters="onApplyFilters"
        @reset-filters="onResetFilters">
        <template #custom="{ item, header }">
          <td v-if="header === 'defaultValue'">
            <template v-if="item.code == MWParams.COMPANY_LOGO">
              <img
                :src="'data:image/jpg;base64,' + item.defaultValue"
                alt="Image"
                class="max-w-sm max-h-16" />
            </template>
            <template v-else-if="item.mwDataType.name === MWDataTypes.BOOLEAN">
              <BaseTableCellStatus
                :item="item.defaultValue == 1"
                :text-true="$t('common.actions.yes')"
                :text-false="$t('common.actions.no')" />
            </template>
            <template v-else-if="isDropdown(item)">
              {{ item.defaultValueString }}
            </template>
            <template v-else>
              {{ item.defaultValue }}
            </template>
          </td>
        </template>
        <template #filters>
          <section class="flex flex-col items-start justify-start w-full max-w-xl py-4 mx-auto">
            <div>
              <label class="space-y-2 text-sm tracking-wide text-gray-400 uppercase">{{
                $t('baseObject.isActive')
              }}</label>
              <BaseTriStateToggle
                :label="$t('baseObject.isActive')"
                name="isActive"
                v-model="filterIsActive" />
            </div>
          </section>
        </template>
      </BaseTable>
    </div>

    <BaseModalEditCreate
      v-if="isModalActive"
      @dismissChanges="onDiscardChanges"
      @save="onSaveModalChanges"
      :isModified="isModifiedParam"
      :hide-save-button="modalMode == 'view'"
      :title="modalMode === 'edit' ? $t('common.actions.edit') : $t('common.actions.view')">
      <template #form>
        <MWParamCreateEdit
          v-model="modifiedParam"
          :value-error-message="getInputErrors('value')"
          :is-horizontal-layout="false"
          :mode="modalMode" />
      </template>
    </BaseModalEditCreate>

    <BaseDrawerConfirm
      :is-active="modalCloseWarningActive"
      @confirm="onDiscardAll"
      @cancel="modalCloseWarningActive = false" />
  </section>
</template>

<script setup lang="ts">
import { computed, onMounted, ref, watch } from 'vue';
import BaseTable from '@/components/common/BaseTable.vue';
import BaseTableCellStatus from '@/components/common/BaseTableCellStatus.vue';
import BaseModalEditCreate from '@/components/common/BaseModalEditCreate.vue';
import BaseButton from '@/components/common/BaseButton.vue';
import BaseDrawerConfirm from '@/components/common/BaseDrawerConfirm.vue';
import type { TableFilterData } from '@/types/Base';
import { filterActive, filterData, sortData } from '@/utils/table';
import { updateParams } from '@/services/api/mw';
import { getCompanyMWParams } from '@/services/api/auth';
import type { MWParam, ParamValue } from '@/types/MW';
import { getGroupItemById } from '@/services/api/groups';
import { useToastStore } from '@/stores/toast';
import { helpers, requiredUnless } from '@vuelidate/validators';
import useVuelidate from '@vuelidate/core';
import MWParamCreateEdit from '@/components/mw/MWParamCreateEdit.vue';
import { v4 as uuidv4 } from 'uuid';
import { deepCopy } from '@/utils/data';
import { useI18n } from 'vue-i18n';
import BaseTriStateToggle from '@/components/common/BaseTriStateToggle.vue';
import { MWDataTypes, MWParams, LocalStorageKeys } from '@/utils/constants';

const toastStore = useToastStore();

const appPreferences = ref<MWParam[]>([]);
const modifiedAppPreferences = ref<MWParam[]>([]);
const isModifiedPreferences = ref<boolean>(false);

const param = ref<MWParam>();
const modifiedParam = ref<MWParam>();
const isModifiedParam = ref<boolean>(false);

const isModalActive = ref(false);
const modalMode = ref<'edit' | 'view'>('edit');
const modalCloseWarningActive = ref(false);
const filterIsActive = ref<boolean | undefined>();

const { t: $t } = useI18n({ useScope: 'global' });
const tableHeaders = [
  { value: 'name', text: $t('baseObject.name'), type: 'text', active: true },
  { value: 'code', text: $t('baseObject.code'), type: 'text', active: true },
  { value: 'remarks', text: $t('material.remarks'), type: 'text', active: true },
  { value: 'defaultValue', text: $t('mw-params.value'), type: 'custom', active: true },
];

onMounted(async () => {
  await loadData();
});

async function loadData(data?: TableFilterData) {
  const response = await getCompanyMWParams();
  appPreferences.value = await fillDropdownValues(response);

  if (data && data.query) {
    appPreferences.value = filterData(appPreferences.value, data.query, [
      'name',
      'code',
      'remarks',
      'defaultValue',
    ]);
  }

  if (data && data.column !== undefined && data.order) {
    appPreferences.value = sortData(appPreferences.value, data.column, data.order, tableHeaders);
  }

  if (data && data.isActive !== undefined) {
    appPreferences.value = filterActive(appPreferences.value, data.isActive);
  }
  modifiedAppPreferences.value = deepCopy<MWParam[]>(appPreferences.value);
}

async function fillDropdownValues(appPreferences: MWParam[]) {
  for (const pref of appPreferences) {
    if (pref.code === MWParams.STANDARDJOBSROOTGROUP && pref.defaultValue) {
      const groupItem = await getGroupItemById(pref.defaultValue);
      pref.defaultValueString = groupItem?.name || '';
    } else if (pref.remarks === '0 = Portrait, 1 = Landscape') {
      pref.defaultValueString = pref.defaultValue === '0' ? 'Portrait' : 'Landscape';
    } else if (pref.mwDataType?.name === MWDataTypes.LIST_SINGLE) {
      pref.defaultValueString = pref.mwParamListValues?.find(
        (x) => x.listValue == pref.defaultValue
      )?.name;
    } else {
      pref.defaultValueString = '';
    }
  }
  return appPreferences;
}

watch(
  () => modifiedParam.value,
  (newData) => {
    isModifiedParam.value = JSON.stringify(newData) !== JSON.stringify(param.value);

    validationModel.value.value = newData?.defaultValue || '';
    $v.value.$touch();
  },
  { deep: true }
);

function onItemClick(item: MWParam) {
  param.value = item;
  modifiedParam.value = deepCopy<MWParam>(param.value);
  isModalActive.value = true;
}

function onDiscardChanges() {
  if (param.value) modifiedParam.value = deepCopy<MWParam>(param.value);
  isModalActive.value = false;
}

async function onSaveModalChanges() {
  $v.value.$touch();
  if ($v.value.$invalid) return;

  if (param.value && modifiedParam.value) {
    param.value.defaultValue = modifiedParam.value.defaultValue;
    param.value.defaultValueString = modifiedParam.value.defaultValueString;
  }

  isModalActive.value = false;
}

watch(
  () => modifiedAppPreferences.value,
  (newData) => {
    isModifiedPreferences.value = JSON.stringify(newData) !== JSON.stringify(appPreferences.value);
  },
  { deep: true }
);

async function onDiscardClick() {
  if (isModifiedPreferences.value) {
    modalCloseWarningActive.value = true;
    return;
  } else {
    await loadData();
  }
}

async function onDiscardAll() {
  modalCloseWarningActive.value = false;
  await loadData();
}

async function onSaveAll() {
  if (modifiedAppPreferences.value) {
    const paramValues: ParamValue[] = [];
    const paramsWithoutCompany: MWParam[] = [];
    const sessionCompanyID = JSON.parse(
      localStorage.getItem(LocalStorageKeys.SessionDetails)!
    )?.companyID;
    modifiedAppPreferences.value.forEach((param) => {
      const originalParam = appPreferences.value.find((p) => p.id === param.id);
      if (originalParam && param.defaultValue !== originalParam.defaultValue) {
        if (sessionCompanyID || param.companyId)
          paramValues.push({
            id: uuidv4(),
            mwParamID: param.id,
            companyID: param.companyId || sessionCompanyID,
            value: param.defaultValue ?? '',
          });
        else {
          paramsWithoutCompany.push(param);
        }
      }
    });

    if (paramValues.length > 0) {
      const res = await updateParams(paramValues);
      res ? toastStore.success({ text: 'Successfully saved' }) : toastStore.error({ text: res });
    } else {
      if (paramsWithoutCompany.length > 0)
        toastStore.warning({
          text: `Company not found for params ${paramsWithoutCompany.map((x) => x.code)}. Changes where not saved`,
          timeout: 6000,
        });
      else toastStore.error({ text: 'Invalid data' });
    }
  }

  await loadData();
}

const validationModel = ref({
  value: modifiedParam.value?.defaultValue || '',
});
const rules = computed(() => ({
  value: {
    required: helpers.withMessage(
      'Value is required',
      requiredUnless(isNumber(modifiedParam.value) || isNullable(modifiedParam.value))
    ),
    validNumeric: helpers.withMessage('Value is not valid', () => checkNumericRestrictions()),
  },
}));
const $v = useVuelidate(rules, validationModel, { $lazy: true });

function checkNumericRestrictions() {
  if (!isNumber(modifiedParam.value)) return true;
  if (modifiedParam.value?.defaultValue == null || modifiedParam.value?.defaultValue === '')
    return false;
  if (modifiedParam.value.mwDataType?.name === MWDataTypes.INTN0)
    return Number(modifiedParam.value.defaultValue) >= 0;

  return true;
}

function isNumber(param?: MWParam) {
  if (param)
    return (
      param.mwDataType?.name === MWDataTypes.INT ||
      param.mwDataType?.name === MWDataTypes.INTN ||
      param.mwDataType?.name === MWDataTypes.INTN0 ||
      param.mwDataType?.name === MWDataTypes.FLOAT ||
      param.mwDataType?.name === MWDataTypes.FLOATN ||
      param.mwDataType?.name === MWDataTypes.FLOATN0 ||
      param.mwDataType?.name === MWDataTypes.INTN0
    );
  return false;
}

function isDropdown(param?: MWParam) {
  if (param)
    return (
      param.code === MWParams.STANDARDJOBSROOTGROUP ||
      param.remarks === '0 = Portrait, 1 = Landscape' ||
      param.mwDataType?.name === MWDataTypes.LIST_SINGLE
    );
  return false;
}

function isNullable(param?: MWParam) {
  if (param) return param.isNullAllowed;
  return false;
}

function getInputErrors(type: 'value') {
  if ($v.value[type].required && $v.value[type].required.$invalid)
    return $v.value[type].required.$message;
  else if ($v.value[type].validNumeric && $v.value[type].validNumeric.$invalid)
    return $v.value[type].validNumeric.$message;
  else return '';
}

function onApplyFilters(data: TableFilterData) {
  data.isActive = filterIsActive.value ?? undefined;
  loadData(data);
}

function onResetFilters() {
  filterIsActive.value = undefined;
  loadData({});
}
</script>
